// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/MemberSpecific/PackageCards.js
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'primereact/dropdown';
import ToastNotification from '../ToastNotification';
import { getIcon, formatKey, formatValue } from './MemberSpecificUtils';
import { getSelectedLanguage } from '../../api/api';
import { fetchStatusOptions, handleStatusChange } from '../../utils/PackageCardsHandlers';

const PackageCards = ({ packages, allowedFields, translations, renderDetailsList, userId }) => {
  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('info');
  const toastRef = useRef(null);

  // Fetch status options from API
  useEffect(() => {
    fetchStatusOptions(setStatusOptions, setIsLoadingStatus, setToastMessage, setToastVariant, setShowToast);
  }, []);

  // Custom renderDetailsList to include Dropdown for status_name
  const customRenderDetailsList = (dataObject, pkg) => {
    if (!dataObject || Object.keys(dataObject).length === 0) {
      return <p className="text-muted">{translations.noDetails || 'No details available'}</p>;
    }

    return (
      <ul className="details-list">
        {Object.entries(dataObject).map(([key, value]) => (
          <li key={key} className="details-item">
            <strong>
              {key === 'status_name' && <FontAwesomeIcon icon={faCheckCircle} className="me-2" />}
              {key !== 'status_name' && getIcon(key) && <FontAwesomeIcon icon={getIcon(key)} className="me-2" />}
              {formatKey(key, translations)}:
            </strong>
            {key === 'status_name' ? (
              <Dropdown
                value={pkg.status_ID}
                options={statusOptions}
                onChange={(e) => handleStatusChange(pkg, e.value, userId, setToastMessage, setToastVariant, setShowToast, statusOptions)}
                placeholder={isLoadingStatus ? 'Loading...' : 'Select Status'}
                disabled={isLoadingStatus || !statusOptions.length || !userId}
                style={{ width: '150px' }}
              />
            ) : (
              <span>{formatValue(value, key, translations, getSelectedLanguage)}</span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />
      {packages && packages.length > 0 ? (
        packages.map((pkg, index) => {
          const packageData = {};
          if (allowedFields && Array.isArray(allowedFields)) {
            allowedFields.forEach((field) => {
              if (pkg[field.key] !== undefined) {
                packageData[field.key] = pkg[field.key];
              }
            });
          } else {
            console.warn('PackageCards: Skipping field filtering due to invalid allowedFields');
          }

          return (
            <div className="card package" key={pkg.package_id || index}>
              <div className="card-header">
                <FontAwesomeIcon icon={faBox} className="me-2" />
                {(translations && translations.package) || 'Package'} {packages.length > 1 ? index + 1 : ''}
              </div>
              <div className="card-body">
                {renderDetailsList ? customRenderDetailsList(packageData, pkg) : (
                  <p className="text-muted">Unable to render package details</p>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="card no-packages">
          <div className="card-body">
            <p className="text-muted">{(translations && translations.noPackages) || 'No package information available'}</p>
          </div>
        </div>
      )}
    </>
  );
};

PackageCards.propTypes = {
  packages: PropTypes.arrayOf(PropTypes.object),
  allowedFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.object
  })),
  translations: PropTypes.object,
  renderDetailsList: PropTypes.func.isRequired,
  userId: PropTypes.number
};

PackageCards.defaultProps = {
  packages: [],
  allowedFields: [],
  translations: {},
  userId: null
};

export default PackageCards;